import type { ItineraryStop, RouteLeg, Trip } from "../types";
import { routeLegNavigationUrl } from "../routeService";
import { Dialog } from "./Dialog";
import { Icon } from "./Icon";

interface RouteDetailsDialogProps {
  trip: Trip;
  leg: RouteLeg | null;
  from: ItineraryStop | null;
  to: ItineraryStop | null;
  onClose: () => void;
}

function minutesLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
}

function sourceLabel(leg: RouteLeg): string {
  if (leg.source === "provider") return "Fresh road route";
  if (leg.source === "cached") return "Cached road route";
  if (leg.source === "planned") return leg.durationMinutes ? "Manual travel buffer" : "Travel buffer required";
  return "Straight-line estimate";
}

export function RouteDetailsDialog({ trip, leg, from, to, onClose }: RouteDetailsDialogProps) {
  const navigationUrl = leg ? routeLegNavigationUrl(trip, leg) : null;
  const navigateLabel = leg?.transportMode === "transit" ? "Check transit in Google Maps" : "Navigate in Google Maps";

  return (
    <Dialog
      open={Boolean(leg && from && to)}
      title="Route details"
      description={from && to ? `${from.name} to ${to.name}` : undefined}
      onClose={onClose}
      className="route-details-dialog"
    >
      {leg ? <div className="route-details">
        <dl>
          <div><dt>Mode</dt><dd>{leg.transportMode}</dd></div>
          <div><dt>Distance</dt><dd>{leg.distanceKm ? `${leg.distanceKm} km` : "Not provided"}</dd></div>
          <div><dt>Travel time</dt><dd>{leg.durationMinutes ? minutesLabel(leg.durationMinutes) : "Required"}</dd></div>
          <div><dt>Source</dt><dd>{sourceLabel(leg)}</dd></div>
          <div><dt>Updated</dt><dd>{new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(leg.fetchedAt))}</dd></div>
        </dl>
        {leg.fallbackReason ? <p className="route-fallback"><strong>Fallback:</strong> {leg.fallbackReason}</p> : null}
        {leg.providerId ? <p className="route-attribution">Routing by OpenRouteService, HeiGIT; map data © OpenStreetMap contributors.</p> : null}
        <div className="dialog-actions">
          <button type="button" className="button button-secondary" onClick={onClose}>Close</button>
          {navigationUrl ? <a className="button button-primary" href={navigationUrl} target="_blank" rel="noreferrer"><Icon name="external" /> {navigateLabel}</a> : null}
        </div>
      </div> : null}
    </Dialog>
  );
}
